<!--

	A reusable banner for short contextual messages.

-->
<template>
	<transition
		name="mia-banner"
		:css="animate"
		appear
	>
		<div
			v-if="isVisible"
			class="mia-Banner"
			:class="[
				`mia-Banner--state-${state}`,
				`mia-Banner--mode-${mode}`,
				{ 'mia-Banner--fullWidth': fullWidth },
			]"
		>
			<div class="mia-BannerIconContainer">
				<inline-svg
					v-if="iconSrc"
					class="mia-BannerIcon"
					:src="iconSrc"
					aria-hidden="true"
				/>
			</div>

			<div class="mia-BannerContent">
				<div v-if="title || $slots.title" class="mia-BannerTitle">
					<slot name="title">
						{{ title }}
					</slot>
				</div>
				<div v-if="description || $slots.description" class="mia-BannerDescription">
					<slot name="description">
						{{ description }}
					</slot>
				</div>
				<div v-if="$slots.action" class="mia-BannerAction">
					<slot name="action" />
				</div>
			</div>

			<button
				v-if="showCloseButton"
				class="mia-BannerCloseButton"
				type="button"
				aria-label="Close message"
				@click="close"
			>
				<span class="mia-BannerCloseIcon" aria-hidden="true" />
			</button>
		</div>
	</transition>
</template>
<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import InlineSvg from "vue-inline-svg";

export type BannerState = "information" | "neutral" | "warning" | "danger" | "success" | "primary";
export type BannerMode = "light" | "dark";

export default defineComponent({
	name: "Banner",
	components: {
		InlineSvg,
	},
	props: {
		show: {
			type: Boolean,
			default: true,
		},
		title: {
			type: String,
		},
		description: {
			type: String,
		},
		showCloseButton: {
			type: Boolean,
			default: false,
		},
		iconSrc: {
			type: String,
		},
		animate: {
			type: Boolean,
			default: false,
		},
		fullWidth: {
			type: Boolean,
			required: false,
			default: false,
		},
		autoHideDurationMs: {
			type: Number,
		},
		state: {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
			type: String as PropType<BannerState>,
			default: "information",
		},
		mode: {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
			type: String as PropType<BannerMode>,
			default: "light",
		},
	},
	emits: ["update:show"],
	data() {
		return {
			autoHideTimeoutId: null as number | null,
			isVisible: false,
		};
	},
	watch: {
		show() {
			this.onShowChanged();
		},
	},
	created() {
		this.onShowChanged();
	},
	beforeUnmount() {
		this.clearAutoHideTimeout();
	},
	methods: {
		onShowChanged() {
			if (!this.show) {
				this.isVisible = false;
				this.clearAutoHideTimeout();
				return;
			}

			this.isVisible = true;
			this.startAutoHideTimeout();
		},
		startAutoHideTimeout() {
			this.clearAutoHideTimeout();
			if (this.autoHideDurationMs == null) {
				return;
			}
			this.autoHideTimeoutId = window.setTimeout(() => {
				this.isVisible = false;
				this.autoHideTimeoutId = null;
				this.$emit("update:show", false);
			}, this.autoHideDurationMs);
		},
		clearAutoHideTimeout() {
			if (this.autoHideTimeoutId == null) {
				return;
			}
			window.clearTimeout(this.autoHideTimeoutId);
			this.autoHideTimeoutId = null;
		},
		close() {
			this.isVisible = false;
			this.clearAutoHideTimeout();
			this.$emit("update:show", false);
		},
	},
});
</script>
<style lang="scss">
.mia-banner-enter-active {
	animation: mia-bannerSlideIn 320ms cubic-bezier(0.22, 1, 0.36, 1);
}

.mia-banner-leave-active {
	animation: mia-bannerSlideOut 220ms ease-in forwards;
}

@keyframes mia-bannerSlideIn {
	0% {
		opacity: 0;
		transform: translateX(100%);
	}
	100% {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes mia-bannerSlideOut {
	0% {
		opacity: 1;
		transform: translateX(0);
	}
	100% {
		opacity: 0;
		transform: translateX(100%);
	}
}

.mia-Banner {
	position: relative;
	display: inline-flex;
	align-items: flex-start;
	justify-content: flex-start;
	gap: theme.$mia-gap-size-md;
	padding: theme.$mia-padding-panel-md;
	box-sizing: border-box;
	background-color: var(--mia-banner-background);
	border-radius: theme.$mia-border-radius;
	outline: theme.$mia-border-width-panel solid var(--mia-banner-border);
	outline-offset: calc(-1 * theme.$mia-border-width-panel);

	&--fullWidth {
		width: 100%;
	}
}

.mia-Banner--state-primary.mia-Banner--mode-light {
	--mia-banner-background: var(--mia-color-primary-25);
	--mia-banner-border: var(--mia-color-primary-100);
	--mia-banner-icon: var(--mia-color-primary-500);
	--mia-banner-title: var(--mia-color-neutral-800);
	--mia-banner-description: var(--mia-color-neutral-500);
	--mia-banner-dismiss: var(--mia-color-neutral-700);
}

.mia-Banner--state-primary.mia-Banner--mode-dark {
	--mia-banner-background: var(--mia-color-primary-850);
	--mia-banner-border: var(--mia-color-primary-850);
	--mia-banner-icon: var(--mia-color-primary-300);
	--mia-banner-title: var(--mia-color-neutral-25);
	--mia-banner-description: var(--mia-color-neutral-200);
	--mia-banner-dismiss: var(--mia-color-neutral-100);
}

.mia-Banner--state-neutral.mia-Banner--mode-light {
	--mia-banner-background: var(--mia-color-neutral-25);
	--mia-banner-border: var(--mia-color-neutral-100);
	--mia-banner-icon: var(--mia-color-neutral-700);
	--mia-banner-title: var(--mia-color-neutral-800);
	--mia-banner-description: var(--mia-color-neutral-500);
	--mia-banner-dismiss: var(--mia-color-neutral-700);
}

.mia-Banner--state-neutral.mia-Banner--mode-dark {
	--mia-banner-background: var(--mia-color-neutral-900);
	--mia-banner-border: var(--mia-color-neutral-800);
	--mia-banner-icon: var(--mia-color-neutral-100);
	--mia-banner-title: var(--mia-color-neutral-25);
	--mia-banner-description: var(--mia-color-neutral-200);
	--mia-banner-dismiss: var(--mia-color-neutral-100);
}

.mia-Banner--state-information.mia-Banner--mode-light {
	--mia-banner-background: var(--mia-color-information-25);
	--mia-banner-border: var(--mia-color-information-100);
	--mia-banner-icon: var(--mia-color-information-600);
	--mia-banner-title: var(--mia-color-neutral-800);
	--mia-banner-description: var(--mia-color-neutral-500);
	--mia-banner-dismiss: var(--mia-color-neutral-700);
}

.mia-Banner--state-information.mia-Banner--mode-dark {
	--mia-banner-background: var(--mia-color-information-800);
	--mia-banner-border: var(--mia-color-information-800);
	--mia-banner-icon: var(--mia-color-information-300);
	--mia-banner-title: var(--mia-color-neutral-25);
	--mia-banner-description: var(--mia-color-neutral-200);
	--mia-banner-dismiss: var(--mia-color-neutral-100);
}

.mia-Banner--state-warning.mia-Banner--mode-light {
	--mia-banner-background: var(--mia-color-warning-25);
	--mia-banner-border: var(--mia-color-warning-200);
	--mia-banner-icon: var(--mia-color-warning-800);
	--mia-banner-title: var(--mia-color-neutral-800);
	--mia-banner-description: var(--mia-color-neutral-500);
	--mia-banner-dismiss: var(--mia-color-neutral-700);
}

.mia-Banner--state-warning.mia-Banner--mode-dark {
	--mia-banner-background: var(--mia-color-warning-900);
	--mia-banner-border: var(--mia-color-warning-800);
	--mia-banner-icon: var(--mia-color-warning-200);
	--mia-banner-title: var(--mia-color-neutral-25);
	--mia-banner-description: var(--mia-color-neutral-200);
	--mia-banner-dismiss: var(--mia-color-neutral-100);
}

.mia-Banner--state-danger.mia-Banner--mode-light {
	--mia-banner-background: var(--mia-color-danger-25);
	--mia-banner-border: var(--mia-color-danger-100);
	--mia-banner-icon: var(--mia-color-danger-500);
	--mia-banner-title: var(--mia-color-neutral-800);
	--mia-banner-description: var(--mia-color-neutral-500);
	--mia-banner-dismiss: var(--mia-color-neutral-700);
}

.mia-Banner--state-danger.mia-Banner--mode-dark {
	--mia-banner-background: var(--mia-color-danger-900);
	--mia-banner-border: var(--mia-color-danger-800);
	--mia-banner-icon: var(--mia-color-danger-300);
	--mia-banner-title: var(--mia-color-neutral-25);
	--mia-banner-description: var(--mia-color-neutral-200);
	--mia-banner-dismiss: var(--mia-color-neutral-100);
}

.mia-Banner--state-success.mia-Banner--mode-light {
	--mia-banner-background: var(--mia-color-success-25);
	--mia-banner-border: var(--mia-color-success-100);
	--mia-banner-icon: var(--mia-color-success-600);
	--mia-banner-title: var(--mia-color-neutral-800);
	--mia-banner-description: var(--mia-color-neutral-500);
	--mia-banner-dismiss: var(--mia-color-neutral-700);
}

.mia-Banner--state-success.mia-Banner--mode-dark {
	--mia-banner-background: var(--mia-color-success-800);
	--mia-banner-border: var(--mia-color-success-800);
	--mia-banner-icon: var(--mia-color-success-300);
	--mia-banner-title: var(--mia-color-neutral-25);
	--mia-banner-description: var(--mia-color-neutral-200);
	--mia-banner-dismiss: var(--mia-color-neutral-100);
}

.mia-BannerIconContainer {
	display: flex;
	align-items: center;
	justify-content: center;
	height: theme.$mia-icon-size;
	flex-shrink: 0;
}

.mia-BannerIcon {
	width: theme.$mia-icon-size;
	height: theme.$mia-icon-size;
	display: block;

	path {
		fill: var(--mia-banner-icon);
	}
}

.mia-BannerContent {
	flex: 1 1 0;
	display: flex;
	flex-direction: column;
	gap: theme.$mia-gap-size-xs;
}

.mia-BannerTitle {
	margin: 0;
	color: var(--mia-banner-title);
	font-family: #{theme.$font-family-sans-serif};
	font-weight: 700;
	line-height: theme.$mia-font-size-label-line-height;
}

.mia-BannerDescription {
	margin: 0;
	color: var(--mia-banner-description);
	font-family: #{theme.$font-family-sans-serif};
	font-weight: 400;
	line-height: theme.$mia-font-size-label-line-height;
	white-space: pre-line;
}

.mia-BannerAction {
	display: flex;
	align-items: center;
	gap: theme.$mia-gap-size-md;
}

.mia-BannerCloseButton {
	flex-shrink: 0;
	width: theme.$mia-icon-size-small;
	height: theme.$mia-icon-size-small;
	padding: 0;
	background: transparent;
	border: none;
	cursor: pointer;
	position: relative;
	margin-top: theme.$mia-margin-size-xs;
}

.mia-BannerCloseIcon {
	display: block;
	width: 100%;
	height: 100%;
	position: relative;
}

.mia-BannerCloseIcon::before,
.mia-BannerCloseIcon::after {
	content: "";
	position: absolute;
	top: theme.$mia-margin-size;
	left: theme.$mia-margin-size-small;
	width: theme.$mia-border-radius-big;
	height: theme.$mia-border-radius-small;
	background: var(--mia-banner-dismiss);
}

.mia-BannerCloseIcon::before {
	transform: rotate(45deg);
}

.mia-BannerCloseIcon::after {
	transform: rotate(-45deg);
}
</style>
